import { ContextMenuAction } from '../../explorer/models/TreeModel';
import { DatabaseObjectTreeNode } from '../../explorer/models/DatabaseTreeModels';
import { toast } from '../../../components/ui/toaster';
import { getConnectionById } from '../../connections/utils/connectionLookup';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { safeConnectionAlias, safeConnectionServer } from '../../../types/safeConnectionDisplay';

/**
 * Generate context menu actions for directory nodes
 */
export function generateDirectoryContextMenuActions(): ContextMenuAction[] {
  return [
    { id: 'copy-name', label: 'Copy directory name', icon: 'clipboard' },
    {
      id: 'script-directory-as',
      label: 'Script directory as',
      icon: 'code',
      submenu: [
        { id: 'script-directory-create', label: 'CREATE', icon: 'file-plus' },
        { id: 'script-directory-drop', label: 'DROP', icon: 'trash' },
        { id: 'script-directory-grant', label: 'GRANT READ/WRITE', icon: 'file-text' },
      ],
    },
    { id: 'separator', label: '', isSeparator: true },
    { id: 'refresh', label: 'Refresh', icon: 'refresh' },
  ];
}

function openDirectoryScriptTab(
  connectionId: string,
  databaseName: string,
  directoryName: string,
  script: string,
  titleSuffix: string,
): void {
  const { activeConnection, connection } = getConnectionById(connectionId);

  if (!activeConnection || !connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();

  addTab({
    title: `${directoryName}_${titleSuffix}-${queryNumber}`,
    type: 'query',
    content: { query: script },
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database: databaseName,
    },
  });
}

/**
 * Execute a directory context menu action
 */
export function executeDirectoryContextMenuAction(
  actionId: string,
  nodeId: string,
  handlers: { onRefresh?: (nodeId: string) => void },
  node?: DatabaseObjectTreeNode,
): void {
  if (actionId === 'refresh') {
    handlers.onRefresh?.(nodeId);
    return;
  }

  if (!node) {
    toast.error({ title: 'Error', description: 'Directory node not found' });
    return;
  }

  const { objectDetails } = node.metadata;
  const directoryName = objectDetails.name;
  const directoryPath = (objectDetails.metadata?.directory_path as string) || '/path/to/directory';
  const connectionId = objectDetails.connectionId;
  const databaseName = (objectDetails.metadata?.database as string) || '';

  switch (actionId) {
    case 'script-directory-create': {
      const script = `CREATE OR REPLACE DIRECTORY "${directoryName}" AS '${directoryPath}';\n`;
      openDirectoryScriptTab(connectionId, databaseName, directoryName, script, 'CREATE');
      break;
    }
    case 'script-directory-drop': {
      const script = `DROP DIRECTORY "${directoryName}";\n`;
      openDirectoryScriptTab(connectionId, databaseName, directoryName, script, 'DROP');
      break;
    }
    case 'script-directory-grant': {
      const script = `GRANT READ, WRITE ON DIRECTORY "${directoryName}" TO <user>;\n`;
      openDirectoryScriptTab(connectionId, databaseName, directoryName, script, 'GRANT');
      break;
    }
    default:
      break;
  }
}
